<!-- Filters -->
<div class="card mb-3">
  <div class="card-body py-3">
    <div class="row g-2 align-items-end">
      <div class="col-lg-3 col-md-6">
        <label class="form-label small mb-1">Status</label>
        <select class="form-select form-select-sm" [ngModel]="statusFilter()" (ngModelChange)="statusFilter.set($event)">
          <option value="all">All Statuses</option>
          @for (opt of statusOptions; track opt.value) {
            <option [value]="opt.value">{{ opt.label }}</option>
          }
        </select>
      </div>
      <div class="col-lg-3 col-md-6">
        <label class="form-label small mb-1">Domain</label>
        <select class="form-select form-select-sm" [ngModel]="domainFilter()" (ngModelChange)="domainFilter.set($event)">
          <option value="all">All Domains</option>
          @for (opt of domainOptions; track opt.value) {
            <option [value]="opt.value">{{ opt.label }}</option>
          }
        </select>
      </div>
      <div class="col-lg-3 col-md-6">
        <label class="form-label small mb-1">Document</label>
        <select class="form-select form-select-sm" [ngModel]="documentFilter()" (ngModelChange)="documentFilter.set($event)">
          <option value="all">All Documents</option>
          @for (doc of documents(); track doc.id) {
            @if (doc.status === 'analyzed') {
              <option [value]="doc.id">{{ doc.file_name }}</option>
            }
          }
        </select>
      </div>
      <div class="col-lg-2 col-md-4">
        <label class="form-label small mb-1">Search</label>
        <input
          type="text"
          class="form-control form-control-sm"
          placeholder="Search findings..."
          [ngModel]="searchQuery()"
          (ngModelChange)="searchQuery.set($event)"
        />
      </div>
      <div class="col-lg-1 col-md-2">
        <button class="btn btn-outline-secondary btn-sm w-100" (click)="clearFilters()">Clear</button>
      </div>
    </div>
  </div>
</div>

<!-- Results Count + Acknowledge Filter -->
<div class="d-flex justify-content-between align-items-center mb-3">
  <span class="text-muted small">{{ resultCount() }} findings</span>
  <div class="btn-group btn-group-sm">
    <button class="btn" [class]="acknowledgeFilter() === 'all' ? 'btn-primary' : 'btn-outline-primary'" (click)="acknowledgeFilter.set('all')">All</button>
    <button class="btn" [class]="acknowledgeFilter() === 'unacknowledged' ? 'btn-primary' : 'btn-outline-primary'" (click)="acknowledgeFilter.set('unacknowledged')">Open</button>
    <button class="btn" [class]="acknowledgeFilter() === 'acknowledged' ? 'btn-primary' : 'btn-outline-primary'" (click)="acknowledgeFilter.set('acknowledged')">Acknowledged</button>
  </div>
</div>

<!-- Findings Table -->
<div class="card">
  <div class="table-responsive">
    <table class="table table-hover mb-0">
      <thead>
        <tr>
          <th style="width: 30px"></th>
          <th class="cursor-pointer" (click)="toggleSort('title')">
            Finding {{ getSortIcon('title') }}
          </th>
          <th class="cursor-pointer" style="width: 130px" (click)="toggleSort('status')">
            Status {{ getSortIcon('status') }}
          </th>
          <th class="cursor-pointer" style="width: 160px" (click)="toggleSort('domain')">
            Domain {{ getSortIcon('domain') }}
          </th>
          <th style="width: 120px">Evidence</th>
        </tr>
      </thead>
      <tbody>
        @for (finding of filteredFindings(); track finding.id) {
          <tr class="cursor-pointer" (click)="toggleRow(finding.id)">
            <td>
              <span class="expand-icon">{{ isRowExpanded(finding.id) ? '&#9660;' : '&#9654;' }}</span>
            </td>
            <td>
              <div class="fw-medium small">{{ finding.title }}</div>
              <div class="text-muted small" style="white-space: normal">{{ finding.normalized_text }}</div>
            </td>
            <td>
              <app-finding-status-badge [status]="finding.status"></app-finding-status-badge>
              @if (isAcknowledged(finding.id)) {
                <span class="badge border border-info text-info fw-normal mt-1" style="font-size: 0.6rem; display: inline-block; background: transparent">Acknowledged</span>
              }
            </td>
            <td>
              <span class="small">{{ getDomainDisplayName(finding.domain) }}</span>
            </td>
            <td>
              @if (finding.evidence_source) {
                <span class="badge bg-light text-dark small">{{ finding.evidence_source }}</span>
              } @else {
                <span class="text-muted small">--</span>
              }
            </td>
          </tr>

          <!-- Expanded Detail Row -->
          @if (isRowExpanded(finding.id)) {
            <tr class="expanded-row">
              <td colspan="5" class="bg-light border-0 px-4 py-3">
                <div class="row g-3">
                  <div class="col-md-6">
                    <h6 class="small fw-bold mb-1">Explanation</h6>
                    <p class="small mb-3">{{ finding.explanation }}</p>

                    @if (finding.original_text) {
                      <h6 class="small fw-bold mb-1">Original Document Text</h6>
                      <blockquote class="small text-muted fst-italic border-start border-3 ps-3 mb-0">
                        "{{ finding.original_text }}"
                      </blockquote>
                    }
                  </div>
                  <div class="col-md-6">
                    <div class="row g-2">
                      <div class="col-6">
                        <h6 class="small fw-bold mb-1">Confidence</h6>
                        <span class="badge bg-light text-dark text-capitalize">{{ finding.confidence }}</span>
                      </div>
                      <div class="col-6">
                        <h6 class="small fw-bold mb-1">Severity</h6>
                        <span class="small text-capitalize" [class]="getSeverityClass(finding.severity)">{{ finding.severity }}</span>
                      </div>
                      @if (finding.source_reference) {
                        <div class="col-6">
                          <h6 class="small fw-bold mb-1">Source Reference</h6>
                          <span class="small">{{ finding.source_reference }}</span>
                        </div>
                      }
                      <div class="col-6">
                        <h6 class="small fw-bold mb-1">Document</h6>
                        <span class="small">{{ getDocumentName(finding.document_id) }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Acknowledgment Info -->
                @if (isAcknowledged(finding.id)) {
                  <div class="mt-3 pt-3 border-top">
                    <h6 class="small fw-bold mb-2 text-info">
                      <span class="me-1">&#10003;</span> Acknowledged
                    </h6>
                    <div class="row g-2">
                      <div class="col-12">
                        <span class="text-muted small">Note:</span>
                        <p class="small mb-1">{{ getAcknowledgment(finding.id)?.note }}</p>
                      </div>
                      @if (getAcknowledgment(finding.id)?.is_compensating_control) {
                        <div class="col-12">
                          <span class="text-muted small">Compensating Control:</span>
                          <p class="small mb-1">{{ getAcknowledgment(finding.id)?.compensating_control_description }}</p>
                        </div>
                      }
                      <div class="col-12">
                        <span class="text-muted small">
                          By {{ getAcknowledgment(finding.id)?.acknowledged_by }} on {{ getAcknowledgment(finding.id)?.acknowledged_at | date: 'mediumDate' }}
                        </span>
                      </div>
                    </div>
                  </div>
                }

                <!-- Acknowledge Action -->
                <div class="d-flex justify-content-end mt-3 pt-2 border-top">
                  @if (isAcknowledged(finding.id)) {
                    <button class="btn btn-sm btn-outline-secondary" (click)="onAcknowledge(finding); $event.stopPropagation()">
                      Edit Acknowledgment
                    </button>
                  } @else {
                    <button class="btn btn-sm btn-outline-primary" (click)="onAcknowledge(finding); $event.stopPropagation()">
                      Acknowledge Gap
                    </button>
                  }
                </div>
              </td>
            </tr>
          }
        } @empty {
          <tr>
            <td colspan="5" class="text-center text-muted py-4">
              No findings match the current filters.
            </td>
          </tr>
        }
      </tbody>
    </table>
  </div>
</div>

<!-- Acknowledge Dialog -->
@if (showAcknowledgeDialog() && selectedFindingForAck()) {
  <app-acknowledge-dialog
    [finding]="selectedFindingForAck()!"
    [existingAcknowledgment]="getAcknowledgment(selectedFindingForAck()!.id)"
    (saved)="onAcknowledgeSaved($event)"
    (cancelled)="onAcknowledgeCancelled()"
  ></app-acknowledge-dialog>
}
